import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { api, del, get, mensagemDeErro } from '../../nucleo/api';
import { nomePessoa, numero } from '../../nucleo/formato';
import { useSessao, veTodasAsEmpresas } from '../../nucleo/sessao';
import { Relogios } from './Relogios';
import { CONTRATOS, ESCOPOS, REGIMES, invalidarPonto, type Configuracao as TipoConfig, type Escopo, type Politica } from './tipos';

const triplo = (v: boolean | null) => (v === null ? '' : v ? 'sim' : 'nao');
const deTriplo = (v: unknown) => (v === 'sim' ? true : v === 'nao' ? false : null);

/** Configuração do ponto: a do workspace (tolerâncias, contratos, padrões) e as políticas por empresa, departamento e funcionário. */
export function Configuracao() {
  const { t } = useTranslation('ponto');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const configurar = s.pode('ponto.configurar');
  const podeWorkspace = configurar && veTodasAsEmpresas(s);
  const [editando, setEditando] = useState(false);
  const [politica, setPolitica] = useState<Politica | 'nova' | null>(null);
  const [remover, setRemover] = useState<Politica | null>(null);
  const q = useQuery({ queryKey: ['ponto', 'configuracao'], queryFn: () => get<TipoConfig>('/ponto/configuracao') });
  const excluir = useMutation({
    mutationFn: (id: string) => del(`/ponto/politicas/${id}`),
    onSuccess: async () => {
      setRemover(null);
      setPolitica(null);
      await invalidarPonto(qc);
    },
    onError: (e) => {
      setRemover(null);
      toast.erro(mensagemDeErro(e));
    },
  });
  const herda = (v: boolean | null) => (v === null ? t('herdar') : v ? t('sim') : t('nao'));
  const colunas: Coluna<Politica>[] = [
    { chave: 'alvo_nome', rotulo: t('campos.alvo'), principal: true, render: (p) => <span className="font-medium">{p.escopo === 'funcionario' ? nomePessoa(p.alvo_nome) : (p.alvo_nome ?? '-')}</span> },
    { chave: 'escopo', rotulo: t('campos.escopo'), render: (p) => <Selo cor="ciano">{t(`escopo.${p.escopo}`)}</Selo> },
    { chave: 'exige_localizacao', rotulo: t('campos.exigeLocalizacao'), render: (p) => herda(p.exige_localizacao) },
    { chave: 'exige_foto', rotulo: t('campos.exigeFoto'), render: (p) => herda(p.exige_foto) },
    { chave: 'cerca_ativa', rotulo: t('campos.cercaAtiva'), render: (p) => (p.cerca_ativa ? `${numero(p.cerca_raio_m)} m` : herda(p.cerca_ativa)) },
    { chave: 'banco_regime', rotulo: t('campos.regime'), render: (p) => (p.banco_regime ? t(`regime.${p.banco_regime}`) : t('herdar')) },
  ];
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {configurar && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setPolitica('nova')}>
            {t('acoes.novaPolitica')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Estado consulta={q}>
        {(c) => (
          <Cartao
            titulo={t('abas.configuracao')}
            acao={
              podeWorkspace ? (
                <button type="button" aria-label={t('acoes.editarConfiguracao')} onClick={() => setEditando(true)} className="rounded p-1 text-tinta-3 hover:text-tinta-1">
                  <Pencil className="size-4" />
                </button>
              ) : undefined
            }
          >
            {configurar && !podeWorkspace && <p className="mb-3 text-sm text-tinta-3">{t('aviso.configTodas')}</p>}
            <dl className="grid gap-x-6 text-sm sm:grid-cols-2">
              {(
                [
                  ['toleranciaMarcacao', `${c.tolerancia_marcacao_min} min`],
                  ['toleranciaDia', `${c.tolerancia_dia_min} min`],
                  ['contratos', c.contratos.map((x) => t(`contrato.${x}`)).join(', ')],
                  ['regimePadrao', t(`regime.${c.banco_regime}`)],
                  ['exigeLocalizacao', c.exige_localizacao ? t('sim') : t('nao')],
                  ['exigeFoto', c.exige_foto ? t('sim') : t('nao')],
                ] as const
              ).map(([k, v]) => (
                <div key={k} className="flex flex-wrap justify-between gap-2 border-b border-borda py-2">
                  <dt className="text-tinta-3">{t(`campos.${k}`)}</dt>
                  <dd className="min-w-0 text-right text-tinta-1">{v}</dd>
                </div>
              ))}
            </dl>
          </Cartao>
        )}
      </Estado>
      <p className="text-sm text-tinta-3">{t('aviso.politica')}</p>
      <Tabela<Politica> tela="ponto.configuracao" caminho="/ponto/politicas" chaveConsulta={['ponto', 'politicas']} colunas={colunas} aoClicar={configurar ? (p) => setPolitica(p) : undefined} vazio={<Vazio texto={t('vazio.politicas')} />} />
      <Relogios />
      {editando && q.data && <EditarConfiguracao atual={q.data} aoFechar={() => setEditando(false)} />}
      {politica && (
        <DefinirPolitica
          politica={politica === 'nova' ? null : politica}
          aoFechar={() => setPolitica(null)}
          aoRemover={politica !== 'nova' ? () => setRemover(politica) : undefined}
        />
      )}
      <Confirmar aberto={!!remover} aoFechar={() => setRemover(null)} aoConfirmar={() => remover && excluir.mutate(remover.id)} titulo={t('confirmar.remover')} texto={t('confirmar.removerTexto')} perigoso carregando={excluir.isPending} />
    </div>
  );
}

function EditarConfiguracao({ atual, aoFechar }: { atual: TipoConfig; aoFechar: () => void }) {
  const { t } = useTranslation('ponto');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const campos: DefCampo[] = [
    { nome: 'tolerancia_marcacao_min', rotulo: t('campos.toleranciaMarcacao'), tipo: 'numero', min: 0, max: 30, passo: 1, largura: 'meia', obrigatorio: true, ajuda: t('campos.toleranciaMarcacaoAjuda') },
    { nome: 'tolerancia_dia_min', rotulo: t('campos.toleranciaDia'), tipo: 'numero', min: 0, max: 60, passo: 1, largura: 'meia', obrigatorio: true, ajuda: t('campos.toleranciaDiaAjuda') },
    ...CONTRATOS.map((c): DefCampo => ({ nome: `contrato_${c}`, rotulo: `${t('campos.contratos')}: ${t(`contrato.${c}`)}`, tipo: 'booleano', largura: 'meia' })),
    { nome: 'banco_regime', rotulo: t('campos.regimePadrao'), tipo: 'selecao', obrigatorio: true, opcoes: REGIMES.map((r) => ({ valor: r, rotulo: t(`regime.${r}`) })) },
    { nome: 'exige_localizacao', rotulo: t('campos.exigeLocalizacao'), tipo: 'booleano', largura: 'meia' },
    { nome: 'exige_foto', rotulo: t('campos.exigeFoto'), tipo: 'booleano', largura: 'meia' },
  ];
  const inicial = { ...atual, ...Object.fromEntries(CONTRATOS.map((c) => [`contrato_${c}`, atual.contratos.includes(c)])) };
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={t('acoes.editarConfiguracao')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-config-ponto">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <Formulario
        id="form-config-ponto"
        campos={campos}
        inicial={inicial}
        aoEnviar={async (v) => {
          await api('PUT', '/ponto/configuracao', {
            tolerancia_marcacao_min: Number(v.tolerancia_marcacao_min),
            tolerancia_dia_min: Number(v.tolerancia_dia_min),
            contratos: CONTRATOS.filter((c) => !!v[`contrato_${c}`]),
            banco_regime: v.banco_regime,
            exige_localizacao: !!v.exige_localizacao,
            exige_foto: !!v.exige_foto,
          });
          await invalidarPonto(qc);
          toast.sucesso(tn('comum.salvo'));
          aoFechar();
        }}
      />
    </Modal>
  );
}

const BUSCA_DO_ESCOPO: Record<Escopo, { caminho: string; rotulo: (i: Record<string, unknown>) => string; pessoa?: boolean }> = {
  empresa: { caminho: '/configuracoes/empresas', rotulo: (i) => String(i.nome_fantasia || i.razao_social || '') },
  departamento: { caminho: '/pessoas/departamentos', rotulo: (i) => String(i.nome ?? '') },
  funcionario: { caminho: '/pessoas/funcionarios', rotulo: (i) => String(i.nome ?? ''), pessoa: true },
};

function DefinirPolitica({ politica: p, aoFechar, aoRemover }: { politica: Politica | null; aoFechar: () => void; aoRemover?: () => void }) {
  const { t } = useTranslation('ponto');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [escopo, setEscopo] = useState<Escopo>(p?.escopo ?? 'empresa');
  const tri = [
    { valor: '', rotulo: t('herdar') },
    { valor: 'sim', rotulo: t('sim') },
    { valor: 'nao', rotulo: t('nao') },
  ];
  const campos: DefCampo[] = [
    ...(p
      ? []
      : [
          { nome: 'escopo', rotulo: t('campos.escopo'), tipo: 'selecao', obrigatorio: true, opcoes: ESCOPOS.map((e) => ({ valor: e, rotulo: t(`escopo.${e}`) })) } as DefCampo,
          { nome: 'alvo_id', rotulo: t(`escopo.${escopo}`), tipo: 'busca', obrigatorio: true, busca: BUSCA_DO_ESCOPO[escopo] } as DefCampo,
        ]),
    { nome: 'exige_localizacao', rotulo: t('campos.exigeLocalizacao'), tipo: 'selecao', largura: 'meia', opcoes: tri },
    { nome: 'exige_foto', rotulo: t('campos.exigeFoto'), tipo: 'selecao', largura: 'meia', opcoes: tri },
    { nome: 'cerca_ativa', rotulo: t('campos.cercaAtiva'), tipo: 'selecao', opcoes: tri },
    { nome: 'cerca_latitude', rotulo: t('campos.latitude'), tipo: 'numero', min: -90, max: 90, passo: 0.000001, largura: 'terco', visivel: (v) => v.cerca_ativa === 'sim' },
    { nome: 'cerca_longitude', rotulo: t('campos.longitude'), tipo: 'numero', min: -180, max: 180, passo: 0.000001, largura: 'terco', visivel: (v) => v.cerca_ativa === 'sim' },
    { nome: 'cerca_raio_m', rotulo: t('campos.raio'), tipo: 'numero', min: 10, max: 100000, passo: 1, largura: 'terco', visivel: (v) => v.cerca_ativa === 'sim' },
    ...(escopo === 'departamento' ? [] : [{ nome: 'banco_regime', rotulo: t('campos.regime'), tipo: 'selecao', opcoes: [{ valor: '', rotulo: t('herdar') }, ...REGIMES.map((r) => ({ valor: r, rotulo: t(`regime.${r}`) }))] } as DefCampo]),
  ];
  const inicial = p
    ? {
        exige_localizacao: triplo(p.exige_localizacao),
        exige_foto: triplo(p.exige_foto),
        cerca_ativa: triplo(p.cerca_ativa),
        cerca_latitude: p.cerca_latitude,
        cerca_longitude: p.cerca_longitude,
        cerca_raio_m: p.cerca_raio_m,
        banco_regime: p.banco_regime ?? '',
      }
    : { escopo, exige_localizacao: '', exige_foto: '', cerca_ativa: '', banco_regime: '' };
  const numeroOuNulo = (v: unknown) => (v === '' || v === null || v === undefined ? null : Number(v));
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      titulo={p ? `${p.escopo === 'funcionario' ? nomePessoa(p.alvo_nome) : (p.alvo_nome ?? '')} · ${t(`escopo.${p.escopo}`)}` : t('acoes.novaPolitica')}
      rodape={
        <>
          {aoRemover && (
            <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={aoRemover}>
              {t('acoes.removerPolitica')}
            </Botao>
          )}
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-politica-ponto">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <Formulario
        key={escopo}
        id="form-politica-ponto"
        campos={campos}
        inicial={inicial}
        aoMudar={(v) => {
          if (!p && ESCOPOS.includes(v.escopo as Escopo) && v.escopo !== escopo) setEscopo(v.escopo as Escopo);
        }}
        aoEnviar={async (v) => {
          const ativa = deTriplo(v.cerca_ativa);
          await api('PUT', '/ponto/politicas', {
              escopo: p?.escopo ?? escopo,
              alvo_id: p?.alvo_id ?? v.alvo_id,
              exige_localizacao: deTriplo(v.exige_localizacao),
              exige_foto: deTriplo(v.exige_foto),
              cerca_ativa: ativa,
              cerca_latitude: ativa ? numeroOuNulo(v.cerca_latitude) : null,
              cerca_longitude: ativa ? numeroOuNulo(v.cerca_longitude) : null,
              cerca_raio_m: ativa ? numeroOuNulo(v.cerca_raio_m) : null,
              banco_regime: (p?.escopo ?? escopo) === 'departamento' ? null : v.banco_regime || null,
            });
          await invalidarPonto(qc);
          toast.sucesso(tn('comum.salvo'));
          aoFechar();
        }}
      />
    </Drawer>
  );
}
